import type { IncludeExcludeState } from '@pulsepointinc/bi-components';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type ICustomFilter,
  MIN_SEARCH_PHRASE_LENGTH,
  SEARCH_DEBOUNCE_TIMEOUT,
  transformIncludeExcludeToStringExpression,
  transformStringExpressionToIncludeExclude,
} from '../../../../../../../helpers';
import { useFilterConfigs } from '../../../../../../../hooks/useFilterConfigs';
import { useFilters } from '../../../../../../../hooks/useFilters';
import type { Filter } from '../../../../../../../stores/filters';
import { useSuggestionsSource } from '../../../../hooks/useSuggestionsSource';
import type { BulkValidationResult } from '../../../BulkUpload';
import type { IncludeExcludeTab } from '../types';
import { useAIVectorSearch } from './useAIVectorSearch';

export type Mode = 'check' | 'block' | undefined;

interface UseIncludeExcludeFilterProps extends ICustomFilter {
  filterName: string;
  onMultiFilterChange?: (filter: Filter) => void;
}

export const useIncludeExcludeFilter = ({
  filterName,
  onChange,
  onExpressionTypeChange,
  onMultiFilterChange,
}: UseIncludeExcludeFilterProps) => {
  const { getFilterConfig } = useFilterConfigs({ disableAutoFetch: true });
  const filterConfig = getFilterConfig(filterName);
  const enableBulkUpload = filterConfig?.enableBulkUpload ?? false;
  const vectorField = filterConfig?.vectorField;
  const useApiSuggestions = filterConfig?.useApiSuggestions ?? false;

  const {
    suggestions: filterSuggestions,
    isLoading,
    onSearchPhraseChange,
  } = useSuggestionsSource(filterName, { useApiSuggestions });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState<IncludeExcludeState>({});
  const [isBulkUploadMode, setIsBulkUploadMode] = useState(false);
  const [activeTab, setActiveTab] = useState<IncludeExcludeTab>('all');
  const [bulkUploadResult, setBulkUploadResult] = useState<BulkValidationResult>();

  const [advancedSearchEnabled, setAdvancedSearchEnabled] = useState(false);

  const { filters, setHideDialogActions } = useFilters();
  const filtersList = useMemo(() => (filters ? Object.values(filters) : []), [filters]);
  const filter = filtersList.find((filter) => filter.filterName === filterName);

  const filterListExpression = filter ? filter.expression : '';

  const {
    handleSearch: handleVectorSearch,
    data: vectorData,
    isSearching: isVectorSearching,
  } = useAIVectorSearch({ vectorField });

  useEffect(() => {
    setHideDialogActions(isBulkUploadMode);
  }, [isBulkUploadMode, setHideDialogActions]);

  useEffect(() => {
    if (!filterListExpression || !onMultiFilterChange) {
      return;
    }

    const transformedValue = transformStringExpressionToIncludeExclude(filterListExpression);
    const expression = transformIncludeExcludeToStringExpression(transformedValue);

    const atLeastOneChecked = Object.values(transformedValue).some((v) => v.check);
    const atLeastOneBlocked = Object.values(transformedValue).some((v) => v.block);

    const expressionType = atLeastOneChecked ? 'target' : atLeastOneBlocked ? 'block' : '';

    const filterToSend: Filter = {
      filterName,
      expression,
      enabled: true,
      expressionType: expressionType || undefined,
    };

    onMultiFilterChange(filterToSend);
  }, []);

  useEffect(() => {
    const transformedValue = filterListExpression
      ? transformStringExpressionToIncludeExclude(filterListExpression)
      : {};

    if (filterListExpression) {
      setFilterState(transformedValue);
    }
  }, [filterListExpression]);

  const suggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < MIN_SEARCH_PHRASE_LENGTH) {
      return filterSuggestions;
    }
    if (isLoading) return [];
    if (advancedSearchEnabled) {
      return vectorData;
    }
    return filterSuggestions;
  }, [vectorData, advancedSearchEnabled, isLoading, searchTerm, filterSuggestions]);

  useEffect(() => {
    setFilterState((currentFilterState: IncludeExcludeState) => {
      const newCategories = suggestions.filter((cat) => !currentFilterState[cat]);

      const newCategoriesState = newCategories.reduce<IncludeExcludeState>((acc, suggestion) => {
        acc[suggestion] = { check: false, block: false };
        return acc;
      }, {});

      return {
        ...currentFilterState,
        ...newCategoriesState,
      };
    });
  }, [suggestions]);

  const performSearch = useCallback(
    (value: string, isAdvancedSearch: boolean) =>
      isAdvancedSearch && value ? handleVectorSearch(value) : onSearchPhraseChange(value),
    [],
  );

  const debouncedOnSearch = useMemo(() => {
    const debounced = debounce((value: string) => {
      if (value.length >= MIN_SEARCH_PHRASE_LENGTH) {
        setSearchTerm(value);
        performSearch(value, advancedSearchEnabled);
      } else {
        setSearchTerm('');
        performSearch('', advancedSearchEnabled);
      }
    }, SEARCH_DEBOUNCE_TIMEOUT);
    return (value: string) => {
      debounced.cancel();
      debounced(value);
    };
  }, [performSearch, advancedSearchEnabled]);

  const toggleAdvanceSearch = useCallback(() => {
    setAdvancedSearchEnabled((currentValue) => {
      const updatedValue = !currentValue;
      if (searchTerm.length >= MIN_SEARCH_PHRASE_LENGTH) {
        performSearch(searchTerm, updatedValue);
      }
      return updatedValue;
    });
  }, [performSearch, searchTerm]);

  const handleFilterChange = useCallback(
    (newFilterState: IncludeExcludeState) => {
      const atLeastOneChecked = Object.values(newFilterState).some((value) => value.check);
      const atLeastOneBlocked = Object.values(newFilterState).some((value) => value.block);

      if (atLeastOneChecked || atLeastOneBlocked) {
        if (atLeastOneChecked) {
          onExpressionTypeChange?.('target');
        } else {
          onExpressionTypeChange?.('block');
        }
      }

      const expression = transformIncludeExcludeToStringExpression(newFilterState);

      onChange?.(expression);

      if (onMultiFilterChange) {
        const filterToSend: Filter = {
          filterName,
          expression,
          enabled: true,
          expressionType: atLeastOneChecked ? 'target' : 'block',
        };
        onMultiFilterChange(filterToSend);
      }

      setFilterState(newFilterState);
    },
    [onChange, onExpressionTypeChange, onMultiFilterChange, filterName],
  );

  const selectedCategories = useMemo(
    () => Object.keys(filterState).filter((category) => filterState[category]?.check || filterState[category]?.block),
    [filterState],
  );

  const mode: Mode = useMemo(() => {
    const nonDefault = Object.keys(filterState).filter(
      (category) => filterState[category]?.check || filterState[category]?.block,
    );

    if (!nonDefault.length) {
      return undefined;
    }

    return nonDefault.some((category) => filterState[category]?.check) ? 'check' : 'block';
  }, [filterState]);

  const handleBulkUploadComplete = useCallback(
    async (result: BulkValidationResult, expressionType: string) => {
      const recognized = result?.recognizedItems ?? [];
      const unrecognized = result?.unrecognizedItems ?? [];

      if (unrecognized.length) {
        setBulkUploadResult({ unrecognizedItems: unrecognized });
      } else {
        setBulkUploadResult(undefined);
      }

      if (recognized.length) {
        const isBlockMode = expressionType === 'block';
        const newState: IncludeExcludeState = recognized.reduce((acc, item) => {
          acc[item] = { check: !isBlockMode, block: isBlockMode };
          return acc;
        }, {} as IncludeExcludeState);
        handleFilterChange({ ...filterState, ...newState });
        setActiveTab('selected');
      }
      setIsBulkUploadMode(false);
    },
    [handleFilterChange, filterState],
  );

  const clearBulkUploadResult = useCallback(() => {
    setBulkUploadResult(undefined);
  }, []);

  return {
    activeTab,
    setActiveTab,
    searchTerm,
    filterState,
    setFilterState,
    advancedSearchEnabled,
    toggleAdvanceSearch,
    suggestions,
    isLoading: isLoading || isVectorSearching,
    mode,
    selectedCategories,
    handleSearchTermChange: debouncedOnSearch,
    handleFilterChange,
    isBulkUploadMode,
    bulkUploadResult,
    clearBulkUploadResult,
    setIsBulkUploadMode,
    handleBulkUploadComplete,
    enableBulkUpload,
    vectorField,
  };
};
